/**
 * Challenges — pure, side-effect-free helpers for the WINDOWED loyalty goal.
 *
 * Two functions, both deterministic (per packages/shared rules) so the whole
 * matrix is unit-tested here rather than exercised through Firestore:
 *
 *   - `isChallengeActive(challenge, nowMs)` — is the challenge live right now?
 *     `active && startMs <= now < endMs`. The `onTicketCollected` pipeline and
 *     the customer-pwa both filter to active challenges with this single
 *     source of truth, so "which challenges count" can never drift between the
 *     award side and the display side.
 *
 *   - `advanceChallengeProgress(...)` — apply ONE paid wash to a customer's
 *     running progress and report whether the criterion is now met. Progress is
 *     a simple counter: +1 visit and +washAmountSen spend, then a threshold
 *     check. The window itself is enforced by the caller (only active
 *     challenges are advanced) — this helper is pure arithmetic.
 */

import type { Challenge, ChallengeCriterion } from '../schemas/challenge.js';

/** Read epoch-ms from a Firestore Timestamp shape (client OR admin OR bare). */
function timestampMs(ts: Challenge['startAt']): number {
  return ts.toMillis?.() ?? ts.seconds * 1000;
}

/**
 * Whether a challenge is live at `nowMs`: switched on AND inside its window.
 * The window is half-open — `startMs <= now < endMs` — so `endAt` is the first
 * instant the challenge is no longer active (mirrors how the dashboard authors
 * it as "runs through the end of the last day → next-midnight boundary").
 */
export function isChallengeActive(
  challenge: Pick<Challenge, 'active' | 'startAt' | 'endAt'>,
  nowMs: number,
): boolean {
  if (!challenge.active) return false;
  const startMs = timestampMs(challenge.startAt);
  const endMs = timestampMs(challenge.endAt);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;
  return startMs <= nowMs && nowMs < endMs;
}

export interface AdvanceChallengeProgressArgs {
  /** The challenge's completion criterion (visits count or spend threshold). */
  readonly criterion: ChallengeCriterion;
  /** Visits counted in the window BEFORE this wash (0 on the first). */
  readonly priorVisits: number;
  /** Sen spent in the window BEFORE this wash (0 on the first). */
  readonly priorSpentSen: number;
  /** This paid wash's amount in integer sen (the same value the points award used). */
  readonly washAmountSen: number;
}

export interface AdvanceChallengeProgressResult {
  /** New windowed visit count (priorVisits + 1). */
  readonly visits: number;
  /** New windowed spend in sen (priorSpentSen + washAmountSen). */
  readonly spentSen: number;
  /** Whether the criterion is met AT OR ABOVE its threshold after this wash. */
  readonly met: boolean;
}

/**
 * Apply one qualifying paid wash to a customer's windowed progress.
 *
 * Adds exactly one visit and this wash's `washAmountSen` to spend, then checks
 * the criterion threshold (`>=`, so hitting exactly `n` / exactly `amountSen`
 * completes it). Priors + amount are normalised to non-negative integers
 * defensively (a corrupt NaN/negative is treated as 0) so a bad stored value
 * can never yield a negative counter or throw.
 */
export function advanceChallengeProgress(
  args: AdvanceChallengeProgressArgs,
): AdvanceChallengeProgressResult {
  const priorVisits =
    Number.isFinite(args.priorVisits) && args.priorVisits > 0 ? Math.floor(args.priorVisits) : 0;
  const priorSpent =
    Number.isFinite(args.priorSpentSen) && args.priorSpentSen > 0
      ? Math.floor(args.priorSpentSen)
      : 0;
  const add =
    Number.isFinite(args.washAmountSen) && args.washAmountSen > 0
      ? Math.floor(args.washAmountSen)
      : 0;

  const visits = priorVisits + 1;
  const spentSen = priorSpent + add;

  const met =
    args.criterion.kind === 'visits'
      ? visits >= args.criterion.n
      : spentSen >= args.criterion.amountSen;

  return { visits, spentSen, met };
}
